import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { formatDate } from 'react-day-picker/moment';
import { DateTime } from 'luxon';
import { useReactToPrint } from 'react-to-print';
import { useGlobalState } from 'state-pool';
import _ from 'lodash';

import Label from '../../../components/label';
import Select from '../../../components/select';
import DayPicker from '../../../components/day-picker';
import Note from '../../../components/note';
import ActionButton from '../../../components/action-button';
import InputNumeral from '../../../components/input-numeral';
import TextNumber from '../../../components/text-number';
import ClickButton from '../../../components/click-button';
import { getData, postData } from '../../../helper/request-response';
import { getRetailBalance } from '../../../helper/cash-balance';
import { onSuccess } from '../../../helper/toaster';
import constant from '../../../config/constant';
import salesPath from './config';
import ComponentToPrint from './print.js';

const Action = () => {

    const componentRef = useRef();
    const handlePrint = useReactToPrint({
        content: () => componentRef.current,
    });

    const data = useLocation();

    const refData = useRef(data);

    const history = useHistory();

    const [, , setRetailBalance] = useGlobalState('retailBalance');

    /**
     * [titleInfo] state
     */
    const [titleInfo, setTitleInfo] = useState("");

    /**
     * [isDisabledSave] state
     */
    const [isDisabledSave, setIsDisabledSave] = useState(true);

    /**
     * [isDisabledAdd]
     */
    const [isDisabledAdd, setIsDisabledAdd] = useState(true);

    const [isPrint, setIsPrint] = useState(true);

    /**
     * [idxItem] state
     */
    const [idxItem, setIdxItem] = useState(-1);

    const [selectedCustName, setSelectedCustName] = useState(constant.DEFAULT_SELECTED);

    const [selectedGnsName, setSelectedGnsName] = useState(constant.DEFAULT_SELECTED);

    /**
     * [sales] state
     */
    const [sales, setSales] = useState({
        id: 0,
        invoiceNo: `RS${DateTime.local().toFormat(constant.INVOICE_FORMAT)}`,
        trxDate: DateTime.local(),
        lineNo: 0,
        description: "",
        isCommit: true,
        customerId: 0,
        customerName: "",
        totalSales: 0,
        payment: 0,
        outstanding: 0,
        apArId: 0,
        items:[]
    });

    const gnsEntity = {
        id: 0,
        code: '',
        name: '',
        purchasePrice: 0,
        sellingPrice: 0,
        qty: 0,
        stock: 0,
        isSubtrahend: false,
        unitId: 0,
        businessUnitId: 2,
        coaId: 0,
        unit: ''
    }

    /**
     * [goodsAndService] state
     */
    const [gns, setGns] = useState(gnsEntity);

    /**
     * Reset state of [goodsAndServices] and [updateItem]
     */
    const resetGns = () => {

        setGns(gnsEntity);

        setSelectedGnsName(name => ({
            ...name,
            label: '',
            value: ''
        }));

    }

    const setGnsState = opt => {

        setGns(gns => ({
            ...gns,
            id: opt.id,
            code: opt.code,
            name: opt.name,
            purchasePrice: opt.purchasePrice,
            sellingPrice: opt.sellingPrice,
            stock: opt.stock,
            isSubtrahend: opt.isSubtrahend,
            unitId: opt.unitId,
            businessUnitId: opt.buId,
            coaId: opt.coaId,
            unit: opt.unit
        }));

    }

    const onInputChange = e => {

        const { name, value } = e.target;

        setSales(sales => ({ 
            ...sales,
            [name]: value
        }));

    };

    /**
     * Autocomplete when name of [GoodsAndService] field are changed
     * @param {object} opt 
     */
    const onGnsNameChange = opt => {

        if (opt.id) {

            setGnsState(opt);
            setSelectedGnsName(name => ({
                ...name,
                value: opt.id,
                label: opt.name
            }));
        } 
    }

    const onSellingPriceChange = values => {

        const { value } = values;

        setGns(gns => ({
            ...gns,
            sellingPrice: parseFloat(value)
        }));
    }

    const onQtyChange = values => {

        const { value } = values;

        const qty = parseFloat(value) > parseFloat(gns.stock) ? parseFloat(gns.stock) : parseFloat(value)

        setGns(gns => ({
            ...gns,
            qty: qty
        }));
    }

    /**
     * Calculate total of purchased items from supplier
     * @param {Array} items 
     */
    const calculateTotal = items => {

        let total = 0;

        for(let i = 0; i < items.length; i++) {

            const qty = parseInt(items[i].qty);
            const sellingPrice = parseInt(items[i].sellingPrice);
            total += qty * sellingPrice;

        }

        return total;

    };

    /**
     * Event triggered when [GoodsAndServices] added to purchased item
     * @param {object} e 
     */
    const onAddItem = e => {

        e.preventDefault();

        /**
         * Add [GoodsAndServices] to purchased items
         */
        const addPurchasedItem = () => {

            let gsItems = sales.items;
            gsItems.push(gns);

            return gsItems;

        }

        /**
         * Update [GoodsAndServices] in purchased items based on [idxItem] state
         */
        const updatePurchasedItem = () => {

            let gsItems = sales.items;

            for (let i = 0; i < gsItems.length; i++) {

                if (i === idxItem) {

                    gsItems[i].id = gns.id;
                    gsItems[i].code = gns.code;
                    gsItems[i].name = gns.name;
                    gsItems[i].sellingPrice = gns.sellingPrice;
                    gsItems[i].qty = gns.qty;

                    break;
                } 
            }

            return gsItems;

        }

        let gsItems = [];

        if (idxItem < 0) gsItems = addPurchasedItem();
        else gsItems = updatePurchasedItem();

        const total = calculateTotal(gsItems);

        setSales(data => ({
            ...data,
            totalSales: total,
            outstanding: total - data.payment,
            items: gsItems
        }));

        resetGns();

        setIdxItem(-1);
    }

    /**
     * Autocomplete when [Customer] field is changed
     * @param {object} opt 
     */
    const onChangeCustomer = async (opt) => {

        if (opt.id) {

            setSales(data => ({ 
                ...data,
                apArId: opt.apArId,
                customerId: opt.id,
                customerName: opt.name
            }));

            setSelectedCustName(name => ({
                ...name,
                value: opt.id,
                label: opt.name
            }));
        } 
    }

    /**
     * Event triggerd when transaction date is selected
     * @param {string} day 
     */
    const onDayChange = day => {

        setSales(data => ({ 
            ...data,
            trxDate: formatDate(day, constant.DEFAULT_DATE)
        }));
    }

    /**
     * Event trigered when item in purchased items is clicked
     * @param {int} index 
     */
    const onViewDetail = (index) => {

        const item = sales.items[index];

        setGns(data => ({
            ...data,
            id: item.id,
            code: item.code,
            name: item.name,
            unit: item.unit,
            unitId: item.unitId,
            stock: item.stock,
            qty: item.qty,
            sellingPrice: item.sellingPrice
        }));

        setSelectedGnsName(name => ({
            ...name,
            value: item.id,
            label: item.name
        }));

        setIdxItem(index);
    }

    /**
     * 
     * @param {object} values
     */
    const onPaymentChange = values => {

        if (isPrint) {

            const { value } = values;

            const gsItems = sales.items;

            const total = calculateTotal(gsItems);

            const outstanding = parseFloat(total) - value;

            setSales(data => ({
                ...data,
                outstanding: outstanding,
                payment: value
            }));

        }
    }

    /**
     * Event trigered when main form of purchase is submited
     * @param {object} e 
     */
    const onSubmit = (e) => {

        e.preventDefault();

        setRetailBalance(0);

        /**
         * Create new purchase data
         */
        const post = async () => {

            const result = await postData(salesPath.base, sales);

            if (!_.isEmpty(result)) {
                onSuccess(`Penjualan ${sales.invoiceNo} berhasil di tambahkan`);

                history.goBack();

                getRetailBalance();
            }
        };

        if (sales.id === 0 && sales.totalSales) post();
        
    }

    const getSalesDetail = async (id) => {

        const result = await getData(`${salesPath.base}/${id}`);

        return result;
    }

    useEffect(() => {

        const init = () => {

            const state = refData.current.state;

            if (state) {
    
                setSales(state);

                setIsPrint(false);
                
                setTitleInfo(`Detail: ${state.invoiceNo}`);

                getSalesDetail(state.id)
                    .then(result => {

                        setSales(data => ({
                            ...data,
                            items: result
                        }));

                    });

                setSelectedCustName(name => ({
                    ...name,
                    value: state.customerId,
                    label: state.customerName
                }));
    
            } else {
    
                setTitleInfo("Tambah Penjualan Baru");
                
            }
        }
    
        init();

    }, []);

    useEffect(() => {

        if (gns.id && gns.name && gns.sellingPrice && gns.qty) setIsDisabledAdd(false);
        else setIsDisabledAdd(true);

        if (sales.customerId === 1) {

            if (sales.invoiceNo && sales.customerId && sales.customerName && sales.trxDate && sales.totalSales && sales.payment) setIsDisabledSave(false);
            else setIsDisabledSave(true);

        } else {

            if (sales.invoiceNo && sales.customerId && sales.customerName && sales.trxDate && sales.totalSales) setIsDisabledSave(false);
            else setIsDisabledSave(true);

        }

    }, [sales.invoiceNo, sales.customerId, sales.customerName, sales.trxDate, sales.totalSales, sales.payment, gns.id, gns.name, gns.sellingPrice, gns.qty]);

    return(
        <React.Fragment>
            { isPrint &&
            <div style={{display:"none"}}>
                <ComponentToPrint 
                    invoiceNo = {sales.invoiceNo}
                    trxDate={`${formatDate(sales.trxDate, 'LL', constant.LOCALE)}`}
                    custName={sales.customerName}
                    totalSales = {sales.totalSales}
                    payment = {sales.payment}
                    outstanding = {sales.outstanding}
                    items = {sales.items}
                    
                    ref={componentRef} />

                    <button onClick={handlePrint}>Print this out!</button>
            </div>
            }
            
            <div className="card-header">
                <h3 className="h6 text-uppercase mb-0">{ salesPath.title } | { titleInfo }</h3>
            </div>
            <div className="card-body">
                <div className="row">

                    <div className="col-md-6 border-right">
                        <form>
                            <div className="form-group row">
                                <div className="col-md-12">
                                    <Label htmlFor="inv-no" text="No Faktur" isMandatory={ true } value={ sales.invoiceNo } />
                                    <input 
                                        id="inv-no" 
                                        type="text" 
                                        className="form-control"
                                        value={ sales.invoiceNo }
                                        disabled />
                                </div>
                            </div>

                            <div className="form-group row">
                                <div className="col-md-12">
                                    <Label htmlFor="cust-name" text="Pelanggan" isMandatory={ true } value={ sales.customerName } />
                                    <Select
                                        id="cust-name"
                                        paramFilter="name"
                                        api={ salesPath.custFind }
                                        entityId="1,2,3,4"
                                        onChange={ onChangeCustomer }
                                        value={ selectedCustName }
                                    />
                                </div>
                            </div>

                            <div className="form-group row">
                                <div className="col-md-12">
                                    <Label htmlFor="trx-date" text="Tanggal" isMandatory={ true } value={ sales.trxDate } />
                                    <div>
                                        <DayPicker id="trx-date" name="trxDate" valueDate={ sales.trxDate } onDayChange={ onDayChange } />
                                    </div>
                                </div>
                            </div>

                            <div className="form-group row">
                                <div className="col-md-12">
                                    <Label htmlFor="description" text="Catatan" />
                                    <div>
                                        <textarea 
                                            id="description"
                                            className="form-control" 
                                            name="description"
                                            value={sales.description}
                                            onChange={ e => onInputChange(e) } 
                                        />
                                    </div>
                                </div>
                            </div>

                        </form>
                    </div>

                    <div className="col-md-6">
                        <form onSubmit={ e => onSubmit(e) }>

                            <div className="form-group row">
                                <div className="col-md-12">
                                    <Label htmlFor="total" text="Total" isMandatory={ true } value={ sales.totalSales } />
                                    <InputNumeral 
                                        id="total"
                                        value={ sales.totalSales }
                                        display={ true }
                                    />
                                </div>
                            </div>

                            <div className="form-group row">
                                <div className="col-md-12">
                                    <Label htmlFor="payment" text="Pembayaran" />
                                    <InputNumeral 
                                        id="payment"
                                        value={ sales.payment }
                                        onValueChange={ onPaymentChange }
                                        isDisabled={ !isPrint }
                                    />
                                </div>
                            </div>

                            {
                                isPrint &&
                                <div className="form-group row">
                                    <div className="col-md-12">
                                        <Label htmlFor="outstanding" text="Sisa Pembayaran" />
                                        <InputNumeral 
                                            id="outstanding"
                                            value={ sales.outstanding }
                                            display={ true }
                                        />
                                    </div>
                                </div>
                            }

                            <div className="form-group row">
                                <div className="col-md-12">
                                    <Label />
                                    <ActionButton 
                                        isSave={ false }
                                        isPrint={ isPrint }
                                        onPrint={handlePrint}
                                        isCancel={ false }
                                        isDisabledSave={ isDisabledSave }
                                        isDisabledAdd={ isDisabledAdd }
                                        addLabel="Tambah Detail Pembelian" />
                                </div>
                            </div>
                        </form>
                    </div>
                </div>

                <br />
                <div className="row">
                    <div className="col-md-6"></div>
                    <div className="col-md-6"><Note isAction={ true } /></div>
                </div>

                {
                    isPrint &&
                    <>
                        <div className="line"></div>
                
                        <div className="row">
                            <div className="col-md-12">
                                <div className="form-group row">
                                    <div className="col-md-6">
                                        <Label htmlFor="gns-name" text="Nama Barang" isMandatory={ true } value={ gns.name } />
                                        <Select
                                            id="gns-name"
                                            api={ salesPath.gnsFind }
                                            entityId="2"
                                            value={ selectedGnsName }
                                            onChange={ onGnsNameChange }
                                        />
                                    </div>

                                    <div className="col-md-4">
                                        <Label htmlFor="selling-price" text="Harga" isMandatory={ true } value={ gns.sellingPrice } />
                                        <InputNumeral 
                                            id="selling-price"
                                            value={ gns.sellingPrice }
                                            onValueChange={ onSellingPriceChange }
                                        />
                                    </div>

                                    <div className="col-md-2">
                                        <Label htmlFor="qty" text="Qty" isMandatory={ true } value={ gns.qty } />
                                        <InputNumeral 
                                            id="qty"
                                            value={ gns.qty }
                                            onValueChange={ onQtyChange }
                                        />
                                    </div>
                                    
                                </div>

                                <div className="form-group row">

                                    <div className="col-md-4">
                                        <Label text="Satuan" />
                                        <input type="text" className="form-control" value={ gns.unit } disabled />
                                    </div>
                                    
                                    <div className="col-md-2">
                                        <Label text="Stock" />
                                        <InputNumeral 
                                            value={ gns.stock }
                                            display={ true }
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <Label />
                                        <div className="btn-group d-flex">
                                            <ClickButton isDisabled={ isDisabledAdd } className="p-2 flex-fill btn btn-success" label={ idxItem === -1 ? `Tambah` : `Perbaharui` } onClick={ onAddItem } />
                                            {
                                                gns.code &&
                                                <ClickButton onClick={ resetGns } className="p-2 flex-fill btn btn-info" />
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                }

                <br />

                {
                    sales.items && 
                    <div className="row">
                        <div className="col-lg-12 mb-4">
                            <div className="table-responsive">
                                <table className="table table-striped table-hover card-text">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Nama Barang</th>
                                            <th>Satuan</th>
                                            <th>Quantity</th>
                                            <th>Harga Jual</th>
                                            <th>Sub Total</th>
                                            {
                                                isPrint &&
                                                <th>Detail</th>
                                            }
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {
                                            sales.items.map((item, index) => {
                                                return(
                                                    <tr key={ index }>
                                                        <th scope="row">{ index + 1 }</th>
                                                        <td>{ item.name }</td>
                                                        <td>{ item.unit }</td>
                                                        <td>{ item.qty }</td>
                                                        <td><TextNumber value={ item.sellingPrice } /></td>
                                                        <td><TextNumber value={ parseInt(item.qty) * parseInt(item.sellingPrice) } /></td>
                                                        {
                                                            isPrint &&
                                                            <td>
                                                                <button className={`btn ${constant.BTN_CLASS_DETAIL}`} onClick={ () => onViewDetail(index) }>{ constant.DETAIL }</button>
                                                            </td>
                                                        }
                                                        
                                                    </tr>
                                                );
                                            })
                                        }
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                }

            </div>
        </React.Fragment>
    );
};

export default Action;
